import { createApi } from '@reduxjs/toolkit/query/react';
import { ImportUsersJob, ImportUsersStatus, User, UserInviteFormValues, UserManagementResponse } from './types';
import baseQuery from '../../../baseQuery/basePharmaNavigatorQuery';
import { handleQueryStarted } from '../../../baseQuery/handleQueryStarted';

const TENANT_USERS_API = 'api/v1/tenant/users';

export const userManagementApi = createApi({
    reducerPath: 'userManagementApi',
    baseQuery,
    tagTypes: ['UserManagement'],
    endpoints: (builder) => ({
        getUserManagementUsers: builder.query<UserManagementResponse, string>({
            query: (query) => decodeURIComponent(`${TENANT_USERS_API}?${query}`),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await handleQueryStarted(_, { dispatch, queryFulfilled });
            },
            providesTags: ['UserManagement'],
        }),
        getUser: builder.query<User, Partial<User>>({
            query: (data) => ({
                url: `${TENANT_USERS_API}/${data.id}`,
            }),
        }),
        updateUser: builder.mutation<User, Partial<User>>({
            query: (data) => ({
                url: `${TENANT_USERS_API}/${data.id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['UserManagement'],
        }),
        inviteUser: builder.mutation<void, UserInviteFormValues>({
            query: (data) => ({
                url: TENANT_USERS_API,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['UserManagement'],
        }),
        lockUser: builder.mutation<User, { id: string }>({
            query: (payload) => ({
                url: `${TENANT_USERS_API}/${payload.id}/lock`,
                method: 'PUT',
            }),
            invalidatesTags: ['UserManagement'],
        }),
        unlockUser: builder.mutation<User, { id: string }>({
            query: (payload) => ({
                url: `${TENANT_USERS_API}/${payload.id}/unlock`,
                method: 'PUT',
            }),
            invalidatesTags: ['UserManagement'],
        }),
        deleteUser: builder.mutation<void, { id: string }>({
            query: (payload) => ({
                url: `${TENANT_USERS_API}/${payload.id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['UserManagement'],
        }),
        importUsersFile: builder.mutation<ImportUsersJob, FormData>({
            query: (formData) => ({
                url: `${TENANT_USERS_API}/import`,
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: ['UserManagement'],
        }),
        getImportUsersStatus: builder.query<ImportUsersStatus, ImportUsersJob>({
            query: ({ jobId }) => ({
                url: `${TENANT_USERS_API}/import/${jobId}/status`,
                method: 'GET',
            }),
            providesTags: ['UserManagement'],
        }),
        getUsersTemplate: builder.query<void, void>({
            query: () => ({
                url: `${TENANT_USERS_API}/import/template`,
                method: 'GET',
                responseHandler: async (response) => {
                    const text = await response.text(); // Wait for the Promise to resolve
                    return text; // Return the resolved text (CSV content)
                },
            }),
            providesTags: ['UserManagement'],
        }),
    }),
});

export const {
    useGetUserManagementUsersQuery,
    useGetUserQuery,
    useUpdateUserMutation,
    useInviteUserMutation,
    useLockUserMutation,
    useUnlockUserMutation,
    useDeleteUserMutation,
    useImportUsersFileMutation,
    useGetImportUsersStatusQuery,
    useGetUsersTemplateQuery,
} = userManagementApi;
